<template>
  <div class="accordion-container">
    <div class="d-flex flex-column mt-4">
      <!-- Timezone Section -->
      <div class="text-h6 font-weight-bold" style="color: #424242;">
        Timezone
      </div>
      <div class="ms-8 mt-4">
        <v-row no-gutters>
          <v-col cols="16">
            <v-select
              v-model="selectedTimezone"
              :items="timezoneOptions"
              density="compact"
              variant="outlined"
              hide-details
              @update:model-value="handleTimezoneChange"
            />
          </v-col>
        </v-row>
      </div>

      <!-- Language Support Section -->
      <div class="text-h6 font-weight-bold mt-8" style="color: #424242;">
        Language Support
      </div>
      <div class="ms-8 mt-4">
        <v-row v-for="code in languageCodes" :key="code" class="language-switch-row">
          <v-col cols="6" class="position-relative">
            <!-- Overlay div only for English -->
            <div 
              v-if="code === 'en-US'" 
              class="switch-overlay"
              @click.prevent
            ></div>
            <v-switch
              v-model="languageSupport[code]"
              :label="languageLabels[code]"
              color="#d2b48c"
              hide-details
              :disabled="isLastEnabledLanguage(code) && code !== 'en-US'"
              @update:model-value="(value) => handleLanguageChange(code, value)"
            />
          </v-col>
        </v-row>
      </div>

      <!-- Facility States Section — Owner only -->
      <div v-if="isOwner" class="text-h6 font-weight-bold mt-8" style="color: #424242;">
        Facility States
      </div>
      <div v-if="isOwner" class="ms-8 mt-4">
        <v-row no-gutters>
          <v-col cols="16">
            <v-select
              v-model="facilityStates"
              :items="facilityStateOptions"
              item-title="title"
              item-value="value"
              multiple
              chips
              density="compact"
              variant="outlined"
              hide-details
              @update:model-value="handleFacilityStatesChange"
            />
            <div class="mt-2" style="font-size: 12px; color: #757575;">
              Select the states where your company operates
            </div>
          </v-col>
        </v-row>
      </div>

      <!-- Autosend SMS Consent Section -->
      <div class="text-h6 font-weight-bold mt-8" style="color: #424242;">
        Autosend SMS Consent When New Voicemail Arrives
      </div>
      <div class="ms-8 mt-2">
        <div class="mb-2" style="font-size: 12px; color: #757575;">
          When enabled, callers who leave a voicemail will automatically receive a text asking for SMS consent.
        </div>
        <v-radio-group
          v-model="autosendSmsConsent"
          inline
          hide-details
          @update:model-value="handleAutosendSmsConsentChange"
        >
          <v-radio :value="true" label="Yes" color="#d2b48c" />
          <v-radio :value="false" label="No" color="#d2b48c" />
        </v-radio-group>
      </div>

    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import axios from 'axios';

export default {
  name: 'LanguageAndOtherOptions',
  components: {},
  props: {
    company: {
      type: Object,
      required: true
    }
  },
  emits: ['update:company', 'show-dialog'],
  setup(props, { emit }) {
    const store   = useStore();
    const isOwner = computed(() => store.state.user?.role === 'Owner');

    // Facility states config
    const facilityStateOptions = [
      { title: 'Washington', value: 'waFacilities' },
      { title: 'California', value: 'caFacilities' },
      { title: 'Idaho',      value: 'idFacilities' },
      { title: 'Oregon',     value: 'orFacilities' },
      { title: 'Texas',      value: 'txFacilities' },
    ];

    const parseFacilityStates = (raw) => {
      if (!raw) return ['waFacilities'];
      return raw.split(',').map(s => s.trim()).filter(Boolean);
    };

    const facilityStates = ref(parseFacilityStates(props.company?.facilityStates));

    // Autosend SMS Consent state
    const autosendSmsConsent = ref(props.company?.autosendSmsConsent ?? false);

    // Timezone state and config
    const selectedTimezone = ref(props.company?.timezone || 'America/New_York');
    const timezoneOptions = [
      'America/New_York',
      'America/Chicago',
      'America/Denver',
      'America/Los_Angeles',
      'America/Anchorage',
      'America/Honolulu'
    ];

    // Language support state and config
    const languageSupport = ref({});
    const languageCodes = ['en-US', 'es-MX', 'ru-RU'];
    const languageLabels = {
      'en-US': 'English',
      'es-MX': 'Spanish',
      'ru-RU': 'Russian'
    };

    const defaultLanguageSupport = {
      'en-US': true,
      'es-MX': true,
      'ru-RU': false
    };

    const isLastEnabledLanguage = (code) => {
      const enabledCount = Object.values(languageSupport.value).filter(Boolean).length;
      return enabledCount === 1 && languageSupport.value[code];
    };

    // Timezone handlers
    const handleTimezoneChange = async (newTimezone) => {
      try {
        const response = await axios.put(
          `/portaldb/db/updateCompanyProperties/${props.company.companyNumber}`,
          { timezone: newTimezone }
        );

        if (response.data.success) {
          emit('update:company', { timezone: newTimezone });
          emit('show-dialog', 'Your timezone has been updated successfully.');
        } else {
          selectedTimezone.value = props.company?.timezone || 'America/New_York';
          emit('show-dialog', 'There was an error saving your timezone. Please try again.');
        }
      } catch (error) {
        console.error('Error saving timezone:', error);
        selectedTimezone.value = props.company?.timezone || 'America/New_York';
        emit('show-dialog', 'There was an error saving your timezone. Please try again.');
      }
    };

    const handleLanguageChange = async (code, value) => {
      try {
        // Create a new object based on current state
        const newLanguageSupport = { ...languageSupport.value };
        newLanguageSupport[code] = value;

        const response = await axios.put(
          `/portaldb/db/updateCompanyProperties/${props.company.companyNumber}`,
          { languageSupport: newLanguageSupport }
        );

        if (response.data.success) {
          languageSupport.value = newLanguageSupport;
          emit('update:company', { 
            ...props.company,
            languageSupport: newLanguageSupport 
          });
        } else {
          // Revert the change
          languageSupport.value[code] = !value;
          emit('show-dialog', 'Error updating language support');
        }
      } catch (error) {
        // Revert the change
        languageSupport.value[code] = !value;
        console.error('Error:', error);
        emit('show-dialog', 'Error updating language support');
      }
    };

    const initializeLanguageSupport = async () => {
      if (!props.company.languageSupport) {
        try {
          const response = await axios.put(
            `/portaldb/db/updateCompanyProperties/${props.company.companyNumber}`,
            { languageSupport: defaultLanguageSupport }
          );
          if (response.data.success) {
            languageSupport.value = { ...defaultLanguageSupport };
            emit('update:company', { languageSupport: defaultLanguageSupport });
          } else {
            emit('show-dialog', 'Error initializing language support');
          }
        } catch (error) {
          console.error('Error initializing language support:', error);
          emit('show-dialog', 'Error initializing language support');
        }
      } else {
        languageSupport.value = { ...props.company.languageSupport };
      }
    };

    const handleFacilityStatesChange = async (newStates) => {
      try {
        const facilityStatesString = newStates.join(', ');
        const response = await axios.put(
          `/portaldb/db/updateCompanyProperties/${props.company.companyNumber}`,
          { facilityStates: facilityStatesString }
        );
        if (response.data.success) {
          emit('update:company', { ...props.company, facilityStates: facilityStatesString });
          emit('show-dialog', 'Facility states updated successfully.');
        } else {
          facilityStates.value = parseFacilityStates(props.company?.facilityStates);
          emit('show-dialog', 'There was an error saving facility states. Please try again.');
        }
      } catch (error) {
        console.error('Error saving facility states:', error);
        facilityStates.value = parseFacilityStates(props.company?.facilityStates);
        emit('show-dialog', 'There was an error saving facility states. Please try again.');
      }
    };

    const handleAutosendSmsConsentChange = async (newValue) => {
      try {
        const response = await axios.put(
          `/portaldb/db/updateCompanyProperties/${props.company.companyNumber}`,
          { autosendSmsConsent: newValue }
        );
        if (response.data.success) {
          emit('update:company', { ...props.company, autosendSmsConsent: newValue });
          emit('show-dialog', 'Autosend SMS consent setting updated successfully.');
        } else {
          autosendSmsConsent.value = props.company?.autosendSmsConsent ?? false;
          emit('show-dialog', 'There was an error saving the setting. Please try again.');
        }
      } catch (error) {
        console.error('Error saving autosend SMS consent setting:', error);
        autosendSmsConsent.value = props.company?.autosendSmsConsent ?? false;
        emit('show-dialog', 'There was an error saving the setting. Please try again.');
      }
    };

    onMounted(() => {
      selectedTimezone.value = props.company?.timezone || 'America/New_York';
      initializeLanguageSupport();
      facilityStates.value = parseFacilityStates(props.company?.facilityStates);
      autosendSmsConsent.value = props.company?.autosendSmsConsent ?? false;
    });

    return {
      // Timezone
      selectedTimezone,
      timezoneOptions,
      handleTimezoneChange,
      // Language Support
      languageSupport,
      languageCodes,
      languageLabels,
      isLastEnabledLanguage,
      handleLanguageChange,
      // Facility States
      isOwner,
      facilityStates,
      facilityStateOptions,
      handleFacilityStatesChange,
      // Autosend SMS Consent
      autosendSmsConsent,
      handleAutosendSmsConsentChange
    };
  }
};
</script>

<style scoped>

.position-relative {
  position: relative;
}

.switch-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1;
  cursor: not-allowed;
}

.switch-overlay:hover {
  background-color: rgba(0, 0, 0, 0.01);
}

.v-row + .v-row  {
  margin-top: -4px;
}

.accordion-container {
  background-color: #f5f5dc;
  padding: 20px;
  border-radius: 8px;
  min-height: 200px;
}

.edit-icon {
  opacity: 0.3;
  transition: opacity 0.2s ease;
}

.edit-icon:hover {
  opacity: 1;
}

.item-text {
  line-height: 20px;
  font-size: 15px;
  color: rgba(0, 0, 0, 0.87);
  font-weight: bold;
}

:deep(.v-text-field input) {
  font-size: 15px !important;
}

:deep(.v-select) {
  width: 100%;
  max-width: 300px;
}

:deep(.v-switch) {
  margin-top: 0;
  margin-bottom: 0;
  min-width: 120px;
}

:deep(.v-switch__label) {
  white-space: nowrap;
  font-size: 15px;
  color: rgba(0, 0, 0, 0.87);
}

:deep(.v-switch--disabled) {
  opacity: 0.6;
}

@media (max-width: 600px) {
  .v-col-6 {
    flex: 0 0 100%;
    max-width: 100%;
  }
}

</style>
